import { useEffect, useState } from "react";
import { resolvePhotoUrl } from "@/lib/api";

/**
 * Renders a client's profile photo with a graceful initials fallback.
 *
 * - Resolves relative `/api/uploads/...` URLs against REACT_APP_BACKEND_URL.
 * - If the image 404s or fails to decode (common when a photo was uploaded in
 *   a different pod whose ephemeral filesystem has since been recycled), the
 *   broken-image alt text is replaced with an initials placeholder so the
 *   directory views stay clean.
 *
 * Style is driven entirely by the consumer via `className` (sized + shaped
 * exactly like the existing inline <img> usages — full-bleed rounded square
 * or circle). `placeholderClassName` lets the consumer match the same
 * size/shape for the fallback.
 */
export function ClientAvatar({
  name,
  photoUrl,
  className = "",
  placeholderClassName = "",
  testId,
}) {
  const resolved = resolvePhotoUrl(photoUrl);
  const [broken, setBroken] = useState(false);

  // Reset the "broken" flag when the underlying URL changes so a re-upload
  // immediately becomes visible without remounting the parent.
  useEffect(() => {
    setBroken(false);
  }, [resolved]);

  const initial = (name || "").trim().charAt(0).toUpperCase() || "·";

  if (!resolved || broken) {
    return (
      <div
        className={placeholderClassName || className}
        data-testid={testId ? `${testId}-fallback` : undefined}
        aria-label={name || "Client"}
      >
        {initial}
      </div>
    );
  }

  return (
    <img
      src={resolved}
      alt={name || ""}
      className={className}
      data-testid={testId}
      onError={() => setBroken(true)}
    />
  );
}
